import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Typography } from "@material-ui/core";
import { Edit } from "@material-ui/icons";
import { Pagination } from "@material-ui/lab";
import { useSnackbar } from 'notistack';
import React from 'react';
import { useHistory } from "react-router-dom";
import SearchField from "../../components/SearchField";
import Page from "../../containers/Page/Page";
import { usePermissions } from "../../hooks/permissions";
import { api } from '../../services/api';
import { Helpers } from '../../utils/Helpers';

interface ISegmento {
    id: string;
    segmento: string;
}

interface IData {
    data: ISegmento[];
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
}

function Segmentos() {

    const history = useHistory();
    const permissions = usePermissions();
    const query = new URLSearchParams(history.location.search);

    const { enqueueSnackbar: alert } = useSnackbar();

    const [error, setError] = React.useState<string>('');
    const [loading, setLoading] = React.useState<boolean>(false);
    const [search, setSearch] = React.useState<string>(query.get('search') || '');
    const [data, setData] = React.useState<IData>();
    const [createDialog, setCreateDialog] = React.useState<boolean>(false);
    const [createLoading, setCreateLoading] = React.useState<boolean>(false);
    const [createValue, setCreateValue] = React.useState<ISegmento>({} as ISegmento);

    /**
     * Ao carregar a página.
     */
    React.useEffect(() => {
        handleFetch();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /**
     * Carregar segmentos.
     * @param page
     * @param visible
     */
    async function handleFetch(page: number = Number(query.get('page')) || 1, visible: boolean = true) {
        setLoading(visible);
        try {
            const url = `/segmentos?search=${search}&page=${page}`;
            const response = await api.get(url);
            history.push(url);
            setData(response.data.object);
            setError('');
        } catch (error: any) {
            setError(Helpers.errorFormat(error));
        }
        setLoading(false);
    }

    /**
     * Salvar segmento.
     */
    async function handleSave() {
        setCreateLoading(true);
        try {
            const response = await api.request({
                url: createValue.id ? `segmentos/${createValue.id}` : 'segmentos',
                method: createValue.id ? 'PUT' : 'POST',
                data: { segmento: createValue.segmento },
            });
            await handleFetch(1, false);
            alert(response.data.message, { variant: 'success' });
        } catch (error) {
            Helpers.error(error, alert);
        } finally {
            setCreateDialog(false);
            setCreateLoading(false);
        }
    }

    /**
     * Abrir dialog de criação.
     * @param segmento 
     */
    function handleCreateSegmento() {
        setCreateValue({ id: '', segmento: '' });
        setCreateDialog(true);
    }

    /**
     * Abrir dialog de edição.
     * @param segmento 
     */
    function handleEditSegmento(segmento: ISegmento) {
        setCreateValue(segmento);
        setCreateDialog(true);
    }

    /**
     * Pesquisar com ENTER.
     * @param event 
     */
    function handleSearchKeyUp(event: React.KeyboardEvent<HTMLTextAreaElement | HTMLInputElement>) {
        if (event.key === 'Enter') {
            handleFetch(1);
        }
    }

    return (
        <Page error={error} loading={loading}>
            <Page.Title>
                <Typography variant="h6" component="div">
                    Segmentos
                </Typography>
                {permissions.has('segmento.store') &&
                    <React.Fragment>
                        <Button
                            color="primary"
                            variant="contained"
                            onClick={() => handleCreateSegmento()}>
                            Novo Segmento
                        </Button>
                        <Dialog fullWidth={true} maxWidth="xs" open={createDialog} onClose={() => setCreateDialog(false)}>
                            <DialogTitle>{createValue.id ? 'Editar Segmento' : 'Novo Segmento'}</DialogTitle>
                            <DialogContent>
                                <TextField
                                    margin="dense"
                                    label="Nome do Segmento"
                                    autoFocus={true}
                                    fullWidth={true}
                                    value={createValue.segmento || ''}
                                    onChange={(event) => setCreateValue({ ...createValue, segmento: event.target.value })}
                                />
                            </DialogContent>
                            <DialogActions>
                                <Button
                                    color="primary"
                                    disabled={createLoading}
                                    onClick={() => setCreateDialog(false)}>
                                    Cancelar
                                </Button>
                                <Button
                                    color="primary"
                                    startIcon={createLoading ? <CircularProgress color="inherit" size={16} /> : undefined}
                                    disabled={createLoading}
                                    onClick={() => handleSave()}>
                                    {createValue.id ? 'Atualizar' : 'Adicionar'}
                                </Button>
                            </DialogActions>
                        </Dialog>
                    </React.Fragment>
                }
            </Page.Title>
            {data &&
                <Page.Section>
                    <Page.Toolbar>
                        <SearchField
                            placeholder="Pesquisa"
                            value={search}
                            onChange={(event) => setSearch(event.target.value)}
                            onKeyUp={(event) => handleSearchKeyUp(event)}
                            onClick={() => handleFetch(1)}
                        />
                    </Page.Toolbar>
                    <Page.Content>
                        <TableContainer>
                            <Table stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell width="5px">ID</TableCell>
                                        <TableCell>Segmento</TableCell>
                                        <TableCell align="center" width="5px">Ações</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {data.data.map((segmento, index) => (
                                        <TableRow key={index} hover={true}>
                                            <TableCell width="5px">{segmento.id}</TableCell>
                                            <TableCell>{segmento.segmento}</TableCell>
                                            <TableCell align="center" width="5px">
                                                <Tooltip arrow title="Editar Segmento">
                                                    <IconButton size="small" color="primary" disabled={!permissions.has('segmento.update')} onClick={() => handleEditSegmento(segmento)}>
                                                        <Edit />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Page.Content>
                    <Box p={2}>
                        <Grid container justifyContent="center">
                            <Pagination
                                page={data.current_page}
                                count={data.last_page}
                                onChange={(_event, page) => handleFetch(page)}
                                color="secondary"
                            />
                        </Grid>
                    </Box>
                </Page.Section>
            }
        </Page >
    );
}

export default Segmentos;